import { useState, type FormEvent } from 'react';
import { Button, Field } from '@promtver/ui-kit';

import { ApiError, isNetworkError } from '../../api/errors';
import { registerEnterprise } from '../../api/registration';
import { ru } from '../../i18n/ru';

/** Требование ТЗ §4.3 к паролю учётной записи. */
const MIN_PASSWORD_LENGTH = 12;
/** Проверка e-mail на клиенте — отсекаем очевидные опечатки до отправки. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** ИНН юридического лица — 10 цифр, ИП — 12. */
const INN_PATTERN = /^\d{10}$|^\d{12}$/;

interface FormState {
  legalName: string;
  contactName: string;
  contactPhone: string;
  email: string;
  password: string;
  passwordRepeat: string;
  district: string;
  inn: string;
  slotsPerWeek: string;
  message: string;
}

const EMPTY_FORM: FormState = {
  legalName: '',
  contactName: '',
  contactPhone: '',
  email: '',
  password: '',
  passwordRepeat: '',
  district: '',
  inn: '',
  slotsPerWeek: '',
  message: '',
};

type FieldName = keyof FormState;
type FieldErrors = Partial<Record<FieldName, string>>;

/**
 * Клиентская проверка формы. Серверные проверки остаются обязательными —
 * клиент лишь подсказывает пользователю заранее, где ошибка.
 */
function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};

  if (!form.legalName.trim()) errors.legalName = ru.registration.requiredField;
  if (!form.contactName.trim()) errors.contactName = ru.registration.requiredField;
  if (!form.contactPhone.trim()) errors.contactPhone = ru.registration.requiredField;

  if (!form.email.trim()) {
    errors.email = ru.registration.requiredField;
  } else if (!EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = ru.registration.emailInvalid;
  }

  if (!form.password) {
    errors.password = ru.registration.requiredField;
  } else if (form.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = ru.registration.passwordShort;
  }

  if (!form.passwordRepeat) {
    errors.passwordRepeat = ru.registration.requiredField;
  } else if (form.passwordRepeat !== form.password) {
    errors.passwordRepeat = ru.registration.passwordMismatch;
  }

  if (form.inn.trim() && !INN_PATTERN.test(form.inn.trim())) {
    errors.inn = ru.registration.innInvalid;
  }

  return errors;
}

/**
 * Разбор деталей ошибки 400: сервер возвращает список невалидных полей.
 * Форматdetails не зафиксирован жёстко, поэтому принимаем и объект с полями,
 * и список строк — всё, что не распознали, показываем общим текстом.
 */
function readServerFieldErrors(details: Record<string, unknown> | undefined): FieldErrors {
  if (!details) return {};

  const source = [details['fields'], details['errors'], details].find(
    (candidate): candidate is Record<string, unknown> =>
      typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate),
  );
  if (!source) return {};

  const errors: FieldErrors = {};
  for (const [key, value] of Object.entries(source)) {
    if (key in EMPTY_FORM && typeof value === 'string' && value.trim()) {
      errors[key as FieldName] = value;
    }
  }
  return errors;
}

export interface RegistrationFormProps {
  /** Возврат к форме входа — после отправки заявки и в подсказках формы. */
  onGoToLogin: () => void;
}

/**
 * Форма регистрации предприятия (FR-B2B-01).
 *
 * Публичная форма: учётной записи у посетителя ещё нет, поэтому заявка
 * уходит без авторизации. Успешная отправка — понятный экран ожидания
 * подтверждения, а не пустая форма.
 */
export function RegistrationForm({ onGoToLogin }: RegistrationFormProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);

  const setField = (name: FieldName) => (value: string) => {
    setForm((previous) => ({ ...previous, [name]: value }));
    setFieldErrors((previous) => ({ ...previous, [name]: undefined }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const errors = validate(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setBusy(true);
    try {
      const result = await registerEnterprise({
        legalName: form.legalName,
        contactName: form.contactName,
        contactPhone: form.contactPhone,
        email: form.email,
        password: form.password,
        district: form.district,
        inn: form.inn,
        slotsPerWeek: form.slotsPerWeek,
        message: form.message,
      });
      setRequestId(result.requestId);
    } catch (cause) {
      if (cause instanceof ApiError) {
        if (cause.code === 'conflict' || cause.status === 409) {
          setError(ru.registration.errorConflict);
        } else if (cause.code === 'validation_error' || cause.status === 400) {
          const serverErrors = readServerFieldErrors(cause.details);
          if (Object.keys(serverErrors).length > 0) {
            setFieldErrors(serverErrors);
          }
          setError(ru.registration.errorValidation);
        } else {
          setError(ru.registration.errorUnknown);
        }
      } else if (isNetworkError(cause)) {
        setError(ru.registration.errorNetwork);
      } else {
        setError(ru.registration.errorUnknown);
      }
    } finally {
      setBusy(false);
    }
  };

  if (requestId !== null) {
    return (
      <div className="flex flex-col gap-3">
        <strong className="text-sm text-success">{ru.registration.successTitle}</strong>
        <p className="text-xs text-muted">{ru.registration.successDescription}</p>
        {requestId ? (
          <p className="text-xs text-muted">
            {ru.registration.successRequestId}: <span className="font-mono">{requestId}</span>
          </p>
        ) : null}
        <Button variant="outline" onClick={onGoToLogin}>
          {ru.registration.successLoginLink}
        </Button>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={(event) => void submit(event)}>
      <Field
        label={ru.registration.legalName}
        value={form.legalName}
        onChange={setField('legalName')}
        placeholder={ru.registration.legalNamePlaceholder}
        error={fieldErrors.legalName}
        required
      />
      <Field
        label={ru.registration.contactName}
        value={form.contactName}
        onChange={setField('contactName')}
        placeholder={ru.registration.contactNamePlaceholder}
        error={fieldErrors.contactName}
        required
      />
      <Field
        label={ru.registration.contactPhone}
        value={form.contactPhone}
        onChange={setField('contactPhone')}
        placeholder={ru.registration.contactPhonePlaceholder}
        inputMode="tel"
        autoComplete="tel"
        error={fieldErrors.contactPhone}
        required
      />
      <Field
        label={ru.registration.email}
        value={form.email}
        onChange={setField('email')}
        type="email"
        inputMode="email"
        autoComplete="email"
        error={fieldErrors.email}
        required
      />
      <p className="text-xs text-muted">{ru.registration.emailHint}</p>
      <Field
        label={ru.registration.password}
        value={form.password}
        onChange={setField('password')}
        type="password"
        autoComplete="new-password"
        error={fieldErrors.password}
        required
      />
      <p className="text-xs text-muted">{ru.registration.passwordHint}</p>
      <Field
        label={ru.registration.passwordRepeat}
        value={form.passwordRepeat}
        onChange={setField('passwordRepeat')}
        type="password"
        autoComplete="new-password"
        error={fieldErrors.passwordRepeat}
        required
      />
      <Field
        label={ru.registration.district}
        value={form.district}
        onChange={setField('district')}
        placeholder={ru.registration.districtPlaceholder}
      />
      <Field
        label={ru.registration.inn}
        value={form.inn}
        onChange={setField('inn')}
        placeholder={ru.registration.innPlaceholder}
        inputMode="numeric"
        error={fieldErrors.inn}
      />
      <Field
        label={ru.registration.slotsPerWeek}
        value={form.slotsPerWeek}
        onChange={setField('slotsPerWeek')}
        placeholder={ru.registration.slotsPerWeekPlaceholder}
      />

      <label className="flex flex-col gap-1.5">
        <span className="text-xs uppercase tracking-wider text-muted">{ru.registration.message}</span>
        <textarea
          value={form.message}
          onChange={(event) => setField('message')(event.target.value)}
          placeholder={ru.registration.messagePlaceholder}
          rows={3}
          className="w-full rounded-field border border-line bg-ink-900 px-3 py-2 text-sm text-content"
        />
      </label>

      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}

      <Button type="submit" tone="accent" disabled={busy}>
        {busy ? ru.registration.submitting : ru.registration.submit}
      </Button>
    </form>
  );
}
